<template>
  <v-breadcrumbs :items="[]" divider="›" density="compact" class="app-breadcrumb">
    <!-- A casinha-GRUPO, e não a casinha: este degrau vai para a lista de ambientes, e não para o
         começo do ambiente em que se está. O `HOME_ICON` é o dono do ícone desse destino, e ele
         vale nos três lugares que levam lá — este degrau, o último item do seletor de ambiente e
         os destinos da barra. Com a casinha simples aqui, o primeiro degrau do caminho prometia o
         ambiente e entregava a lista. -->
    <v-breadcrumbs-item :to="{name: 'home'}" :title="$tt('workspaces-all')">
      <v-icon size="small">{{ HOME_ICON }}</v-icon>
    </v-breadcrumbs-item>
    <template v-for="crumb of crumbs" :key="(crumb.label || '') + '\u0000' + crumb.text">
      <v-breadcrumbs-divider/>
      <v-breadcrumbs-item :to="crumb.to">
        <!-- Sem rótulo, a palavra É o degrau e ocupa uma linha só: um span vazio na pilha
             vertical empurraria o valor para baixo e desalinharia este degrau dos vizinhos. É o
             mesmo `v-if` que o ShellMenu usa, pelo mesmo motivo. -->
        <span class="shell-stack">
          <span v-if="crumb.label" class="shell-label">{{ crumb.label }}</span>
          <!-- Sem rótulo, o degrau É um rótulo: CRIAR e EDITAR não nomeiam uma coisa, dizem o
               que se está fazendo com o degrau de cima, e é a tipografia de rótulo — caixa alta,
               esmaecida, peso normal — que diz isso. Vestir a do valor fazia a palavra parecer o
               nome de um item chamado "Editar". O `shell-step` devolve a ela o CORPO do valor:
               miúda como um rótulo ela sumia, porque rótulo tem um valor embaixo sustentando, e
               este degrau não tem.

               A condição é a MESMA do span acima, de propósito: há um estado só, "este degrau
               tem rótulo?", e as duas decisões saem dele. -->
          <span :class="crumb.label ? 'shell-value' : 'shell-label shell-step'">{{ crumb.text }}</span>
        </span>
      </v-breadcrumbs-item>
    </template>
  </v-breadcrumbs>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {breadcrumb, Crumb} from '@/utils/breadcrumb'
import {EnhancedWorkpace} from '@/types/Workspace'
import {HOME_ICON} from '@/utils/destinations'

/**
 * O caminho até a tela atual, com cada degrau clicável.
 *
 * Os degraus vêm pelo SLOT, e não pela prop `items`: o VBreadcrumbs marca
 * `disabled` no último item quando ele mesmo monta a lista (VBreadcrumbs.js,
 * `index >= array.length - 1`), e aqui todos precisam continuar clicáveis. O
 * `:items="[]"` é só para ele não montar nada por conta própria.
 *
 * A regra de quais degraus existem está em utils/breadcrumb.ts, compartilhada com as
 * abas de coleção — é o que garante que clicar na aba e clicar no degrau levem ao
 * mesmo lugar.
 */
@Component({name: 'ontos-app-breadcrumb'})
class AppBreadcrumb extends Vue {
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace | null
  /**
   * O nome do item aberto, publicado pela tela. Sem ele o degrau do item não é
   * desenhado — ver o porquê em utils/breadcrumb.ts.
   */
  @Prop({type: String, default: null}) readonly instanceLabel!: string | null
  //Exposta ao template: constante de módulo não é enxergada de lá.
  HOME_ICON = HOME_ICON

  get crumbs(): Crumb[] {
    return breadcrumb(this.$route, this.workspace, this.instanceLabel)
  }
}

export default toNative(AppBreadcrumb)
</script>

<style scoped>
/* Uma linha só, e o que não couber some pela direita — o degrau mais à esquerda é o
   que menos pode sumir, porque é o caminho de volta. */
.app-breadcrumb {
  flex-wrap: nowrap;
  overflow: hidden;
  padding: 0;
}

/* O par rótulo/valor de cada degrau é o idioma `shell-*`, global e compartilhado com os
   seletores da barra — ver o <style> do App.vue. */
</style>
